import { type FC, type FunctionComponent, type SVGProps } from 'react';
import { MfaMethod } from '@shared/services';
import styles from './Mfa.module.scss';
import { ENROLL_DESCRIPTION, METHOD_LABEL, VERIFY_DESCRIPTION } from './labels';
import PasskeyIcon from './assets/passkey.svg?react';
import TotpIcon from './assets/totp.svg?react';
import EmailIcon from './assets/email.svg?react';
import { Card } from '@/components/ui';

const METHOD_ICON: Record<MfaMethod, FunctionComponent<SVGProps<SVGSVGElement>>> = {
  [MfaMethod.PASSKEY]: PasskeyIcon,
  [MfaMethod.TOTP]: TotpIcon,
  [MfaMethod.EMAIL]: EmailIcon,
};

interface Props {
  methods: MfaMethod[];
  mode: 'enroll' | 'verify';
  onSelect: (method: MfaMethod) => void;
  /** Disables the list while a ceremony fired from a selection (e.g. passkey) is in flight. */
  busy?: boolean;
}

export const MethodPicker: FC<Props> = ({ methods, mode, onSelect, busy = false }) => {
  const isEnroll = mode === 'enroll';
  const descriptions = isEnroll ? ENROLL_DESCRIPTION : VERIFY_DESCRIPTION;

  return (
    <>
      <Card.Title
        title={isEnroll ? 'Set up two-factor authentication' : 'Two-factor authentication'}
        description={
          isEnroll
            ? 'Your account requires a second factor. Choose a method to set up.'
            : 'Choose how you want to verify your identity.'
        }
      />
      <div className={styles.methodList}>
        {methods.map((method) => {
          const MethodIcon = METHOD_ICON[method];

          return (
            <button
              key={method}
              type="button"
              className={styles.methodButton}
              onClick={() => onSelect(method)}
              disabled={busy}
              data-testid={`mfa-method-${method}`}
            >
              <span className={styles.methodIcon}>
                <MethodIcon />
              </span>
              <span className={styles.methodText}>
                <span className={styles.methodLabel}>{METHOD_LABEL[method]}</span>
                <span className={styles.methodDescription}>{descriptions[method]}</span>
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
};
